import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { usePreloading } from '../hooks/usePreloading';
import Logger from '../utils/intelligentLogger';

interface PreloadingNavLinkProps {
  to: string;
  className?: string | ((props: { isActive: boolean }) => string);
  onClick?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  'aria-label'?: string;
}

/**
 * Preloading-aware NavLink that provides instant navigation
 */
const PreloadingNavLink: React.FC<PreloadingNavLinkProps> = ({
  to,
  className,
  onClick,
  children,
  style,
  'aria-label': ariaLabel,
}) => {
  const navigate = useNavigate();
  const { getPreloadedPage } = usePreloading();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const startTime = Date.now();
    
    // Check if page is preloaded
    const preloadedPage = getPreloadedPage(to);
    
    if (preloadedPage) {
      // Instant navigation from cache
      Logger.throttle(`nav-${to}`, `Instant navigation to ${to} (from cache)`);
      e.preventDefault();
      navigate(to);
      
      // Track performance for cached navigation
      const loadTime = Date.now() - startTime;
      Logger.performance.trackNavigation(to, loadTime);
      
      import('../services/PreloadingService').then(({ default: preloadingService }) => {
        preloadingService.trackNavigation(to, startTime);
      });
    } else {
      // Normal navigation (fallback) - only log in debug mode
      Logger.debug(`Loading ${to} normally (not cached)`);
    }

    // Call original onClick if provided
    if (onClick) {
      onClick();
    }
  };

  return (
    <NavLink
      to={to}
      className={className}
      onClick={handleClick}
      style={style}
      aria-label={ariaLabel}
    >
      {children}
    </NavLink>
  );
};

export default PreloadingNavLink; 